import React, { MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  List,
  ListItemIcon,
  Stack,
  Box,
  IconButton,
  DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EastIcon from '@mui/icons-material/East';
import { staticFileLocation } from '@server/constants';
import theme from '@styles/theme';
import { darkBlue2, successGreen, primaryBlue, lightGrey2 } from '@styles/colors';
import { bold } from '@styles/fontWeights';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdateStatus } from '@features/profile/hooks/useUpdateStatus';
import { useUpdateWalkthroughStatus } from '@features/profile/hooks/useUpdateWalkthroughStatus';
import { toast } from 'react-toastify';
import { useGetCurrentPlan } from '@features/payments/hooks/useGetCurrentPlan';
import { DotIcon } from '../../../../vectors/index';
import { getCookie, setCookie } from '@utils/getCookie';
import { updateWalkthroughStatus } from '@utils/updateOnboardingStatus';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { selectedPlanName } from '@utils/pricing';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';

interface BetterHiringChecklistProps {
  open: boolean;
  handleClose: () => void;
}
type CheckListStep = {
  title: string;
  eventName?: string;
  isChecked?: boolean;
  description: string;
  buttonText?: string;
  buttonAction?: () => void;
  items: {
    type: string;
    eventName?: string;
    action: () => void;
    hasAction: boolean;
  }[];
};

const ChecklistStep = ({
  step,
  index,
  selectedPlan,
  handleCheckBoxClick,
}: {
  step: CheckListStep;
  index: number;
  selectedPlan?: string;
  handleCheckBoxClick: (index: number) => void;
}) => {
  const inboxChecked = getCookie('inboxChecked');
  const [alertText, setAlertText] = useState('');
  const [isChecked, setIsChecked] = useState(inboxChecked ? true : false);
  const { updateStatus } = useUpdateStatus({
    setAlertText,
  });
  const { authData } = useContext(authContext);
  const isStepChecked =
    step.buttonText !== 'Disabled'
      ? step?.isChecked
        ? 'primary'
        : 'disabled'
      : isChecked
        ? 'primary'
        : 'disabled';
  return (
    <List>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            width="100%"
            spacing={3}
            alignItems="center"
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                backgroundColor:
                  isStepChecked === 'primary'
                    ? 'lightGray !important'
                    : alpha(theme.palette.dark.main, 0.1),
                color:
                  isStepChecked === 'primary' ? 'gray !important' : darkBlue2,
                display: 'inline-block',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              {step.title} {index + 1}
            </Typography>
            <Typography
              color={
                isStepChecked === 'primary' ? 'lightGray !important' : 'gray'
              }
            >
              {step.description}
            </Typography>
            {step.buttonText &&
              step.buttonText !== 'Disabled' &&
              step.buttonText === 'Copy My Link' && (
                <Button
                  size="small"
                  variant={index !== 3 ? 'contained' : 'text'}
                  color="primary"
                  disabled={isStepChecked === 'primary' ? true : false}
                  onClick={step.buttonAction}
                  sx={{
                    fontWeight: index === 3 ? bold : '',
                    color: index === 3 ? darkBlue2 : '',
                    fontStyle: index === 3 ? 'italic' : '',
                    textWrap: index === 3 ? 'nowrap' : '',
                    fontSize: '0.8rem !important',
                  }}
                >
                  {step.buttonText}
                </Button>
              )}
            {step.buttonText &&
              step.buttonText !== 'Disabled' &&
              step.buttonText !== 'Copy My Link' && (
                <IconButton
                  onClick={step.buttonAction}
                  size="small"
                  sx={{
                    padding: '3px !important',
                    border: `1px solid ${isStepChecked === 'primary' ? lightGrey2 : primaryBlue}`,
                    borderRadius: '50%',
                    ml: 2,
                  }}
                  disabled={isStepChecked === 'primary' ? true : false}
                >
                <EastIcon sx={{ fontSize: '14px', color: isStepChecked === 'primary' ? lightGrey2 : darkBlue2 }} />
                </IconButton>
              )}
          </Stack>
          <Box mt={2}>
            {step.items.map((item, index) => (
              <Box
                display="flex"
                sx={{ alignItems: 'center !important' }}
                ml={5}
              >
                <DotIcon />
                <Stack
                  maxWidth="590px"
                  key={index}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    color={
                      isStepChecked === 'primary'
                        ? 'lightGray !important'
                        : 'gray'
                    }
                    dangerouslySetInnerHTML={{ __html: item.type }}
                  />
                  {step.buttonText !== 'Copy My Link' &&
                  step.buttonText !== 'Disabled' &&
                  !item.hasAction ? (
                    <IconButton
                      onClick={item.action}
                      size="small"
                      sx={{
                        padding: '3px !important',
                        border: `1px solid ${isStepChecked === 'primary' ? lightGrey2 : primaryBlue}`,
                        borderRadius: '50%',
                        ml: 2,
                      }}
                        disabled={isStepChecked === 'primary' ? true : false}
                    >
                        <EastIcon sx={{ fontSize: '14px', color: isStepChecked === 'primary' ? lightGrey2 : darkBlue2 }} />
                    </IconButton>
                  ) : (
                    step.buttonText !== 'Copy My Link' &&
                    step.buttonText !== 'Disabled' &&
                    item.hasAction && (
                      <Typography
                        sx={{
                          fontWeight: bold,
                          ml: 2,
                          color: successGreen,
                          fontStyle: 'italic',
                          textWrap: 'nowrap',
                        }}
                      >
                        Done
                      </Typography>
                    )
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
        <ListItemIcon>
          {step?.isChecked || (isChecked && step.buttonText == 'Disabled') ? (
            <CheckCircleIcon color={isStepChecked} />
          ) : (
            <Button
              onClick={() => {
                track(
                  `${amplitudeEventsName.MARK_COMPLETE_STEP_BUTTON}-${index + 1}-button-${selectedPlan}`,
                );
                if (step.buttonText !== 'Disabled') {
                  handleCheckBoxClick(index);
                } else {
                  setIsChecked(true);
                  setCookie('inboxChecked', 'true', 5);
                  updateWalkthroughStatus(
                    authData?.user?.company?.plan,
                    authData?.user,
                    undefined,
                    updateStatus,
                  );
                }
              }}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', color: darkBlue2 }}
            >
              Mark as Complete
            </Button>
          )}
        </ListItemIcon>
      </Stack>
    </List>
  );
};

const BetterHiringChecklist: React.FC<BetterHiringChecklistProps> = ({
  open,
  handleClose,
}) => {
  const navigate = useNavigate();

  const { authData } = useContext(authContext);
  const [alertText, setAlertText] = useState('');
  const { currentPlanData, isCurrentPlanLoading } = useGetCurrentPlan();
  const { updateStatus } = useUpdateStatus({
    setAlertText,
  });
  const { updateProfile } = useUpdateProfile({});
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const inboxChecked = getCookie('inboxChecked');
  const [isChecked, setIsChecked] = useState(inboxChecked ? true : false);
  const { updateWalkthroughStatus } = useUpdateWalkthroughStatus();
  const handleCopyCodeSnippet = () => {
    track(
      `${amplitudeEventsName.CHECKLIST_HIRING_LINK_COPY_BUTTON}-${selectedPlan}`,
    );
    updateStatus({ has_shared_page: true });
    navigator.clipboard.writeText(
      `${import.meta.env.VITE_MARKETING_SITE_URL}${authData.user?.company?.s_slug}?src=social`,
    );
    toast.success('Link copied to clipboard');
  };
  const handleButtonClick = () => {
    setIsChecked(true);
    setCookie('inboxChecked', 'true', 5);
  };

  const steps: CheckListStep[] = [
    {
      title: 'Step',
      description: 'Review your hiring page here',
      buttonText: 'Do it here',
      isChecked: false,
      buttonAction: () => {
        if (authData?.user?.company) {
          track(
            `${amplitudeEventsName.CHECKLIST_HIRING_PAGE_BUTTON}-${selectedPlan}`,
          );
          window.location.href = `${import.meta.env.VITE_MARKETING_SITE_URL}/${authData.user.company.s_slug}`;
        }
      },

      items: [
        {
          type: 'Add your company logo & background image here',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_ADD_LOGO_LINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/account#Company-Profile');
          },
        },
        {
          type: 'Review/update job descriptions & add more jobs here',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_JOB_DESCRIPTION_LINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/positions/');
          },
        },
        {
          type: 'Review/add prequalifying questions & automated tags here',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_QUALIFYING_QUESTIONS_lINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/application-questions/');
          },
        },
      ],
    },
    {
      title: 'Step',
      isChecked: false,
      description: 'Setup hiring automation',
      items: [
        {
          type: 'First, set up your prequalifying questions here.',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_SETUP_QUALIFYING_QUESTIONS_LINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/application-questions/');
          },
        },
        {
          type: 'Next, set up interviews on your calendar here.',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_SETUP_INTERVIEWS_LINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/calendar/');
          },
        },
        {
          type: 'Last, turn on auto-save & auto interview here.',
          hasAction: false,
          action: () => {
            track(
              `${amplitudeEventsName.CHECKLIST_AUTOSAVE_LINK_BUTTON}-${selectedPlan}`,
            );
            navigate('/employer/positions/');
          },
        },
      ],
    },
    {
      title: 'Step',
      isChecked: false,
      description: 'Add a hiring link to your website',
      buttonText: 'Copy My Link',
      buttonAction: () => handleCopyCodeSnippet(),
      items: [
        {
          type: 'Copy the link & add it to your website in the header, footer, or anywhere you want to make it easy for applicants to apply, <b><i>OR</i></b>',
          hasAction: false,
          action: () => handleCopyCodeSnippet(),
        },
        {
          type: 'Email your web team, copy support@staffedup.com & we’ll help get it done for you.',
          hasAction: false,
          action: () => handleCopyCodeSnippet(),
        },
      ],
    },
    {
      title: 'Step',
      isChecked: false,
      description: 'Set up your new hiring onboarding here',
      buttonText: 'Do it here',
      buttonAction: () => {
        track(
          `${amplitudeEventsName.CHECKLIST_SETUP_ONBOARDING_LINK_BUTTON}-${selectedPlan}`,
        );
        navigate('/employer/account/onboarding', {
          state: { onboarding: true },
        });
      },
      items: [],
    },
  ];
  const [stepArray, setStepArray] = React.useState<CheckListStep[]>(steps);

  const handleCheckBoxClick = (index: number) => {
    const updatedSteps = [...stepArray];
    updatedSteps[index].isChecked = true;
    setStepArray(updatedSteps);
    if (index === 0) {
      updateStatus({
        has_image: true,
      });
      updateProfile({
        has_visited_job_posting_page:  true,
        has_visited_qualification_question_page: true,
      })
    }
    if (index === 1 && currentPlanData?.object?.plan_int === 4) {
      updateStatus({
        has_shared_page: true,
      });
    } else if (index === 1) {
      updateStatus({
        has_facebook_page_id: true,
      });
    }
    if (index === 2) {
      updateStatus({
        has_shared_page: true,
      });
    }
    if (index === 3) {
      updateStatus({
        has_mission_statement: true,
      });
    }
  };

  useEffect(() => {
    if (authData?.user?.company) {
      const updatedSteps = steps.map((step, index) => {
        if (index === 0) {
          return {
            ...step,
            items: step.items.map((item, idx) =>
              idx === 0
                ? {
                    ...item,
                    hasAction:
                      authData?.user?.company?.s_cover_image_file &&
                      authData?.user?.company?.s_logo_file
                        ? true
                        : false,
                  }
                : idx === 1
                  ? {
                      ...item,
                    hasAction: authData?.user?.company?.check_list?.has_visited_job_posting_page
                        ? true
                        : false,
                    }
                  : idx === 2
                    ? {
                        ...item,
                      hasAction: authData?.user?.company?.check_list?.has_visited_qualification_question_page
                          ? true
                          : false,
                      }
                    : item,
            ),
            isChecked:
              authData?.user?.has_image &&
              authData?.user?.company?.check_list?.has_visited_job_posting_page &&
              authData?.user?.company?.check_list?.has_visited_qualification_question_page,
          };
        }
        if (index === 1) {
          return {
            ...step,
            items: step.items.map((item, idx) =>
              idx === 0
                ? {
                    ...item,
                  hasAction: authData?.user?.company?.check_list?.has_added_pre_qualification_questions
                      ? true
                      : false,
                  }
                : idx === 1
                  ? {
                      ...item,
                      hasAction: authData?.user?.calendar_count ? true : false,
                    }
                  : idx === 2
                    ? {
                        ...item,
                        hasAction:
                          authData?.user?.company?.auto_save_applications &&
                          authData?.user?.company?.auto_interview_invite
                            ? true
                            : false,
                      }
                    : item,
            ),
            isChecked:
              (authData?.user?.company?.check_list?.has_added_pre_qualification_questions &&
                authData?.user?.company?.auto_save_applications &&
                authData?.user?.calendar_count &&
                authData?.user?.company?.auto_interview_invite) ||
              authData?.user?.has_facebook_page_id,
          };
        }
        if (index === 2) {
          return {
            ...step,
            items: step.items.map((item, idx) =>
              idx === 0
                ? {
                    ...item,
                    hasAction: authData?.user?.has_shared_page ? true : false,
                  }
                : item,
            ),
            isChecked: authData?.user?.has_shared_page,
          };
        }
        if (index === 3) {
          return {
            ...step,
            items: step.items.map((item, idx) =>
              idx === 0
                ? {
                    ...item,
                    hasAction: authData?.user?.package_template ? true : false,
                  }
                : item,
            ),
            isChecked:
              authData?.user?.package_template ||
              authData?.user?.has_mission_statement,
          };
        }
        return step;
      });

      setStepArray(updatedSteps);
    }
  }, [authData?.user]);

  useEffect(() => {
    track(amplitudeEventsName.VIEWD_SCREEN_CHECKLIST);
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        align="center"
        sx={{
          backgroundColor: primaryBlue,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            width: '100%',
            height: '43px',
            zIndex: 0,
          },
        }}
      >
        <img
          src={`${staticFileLocation}img/logos/LogoStampShadow.png`}
          alt="Notifications"
          style={{
            width: 60,
            height: 60,
            objectFit: 'contain',
            marginRight: theme.spacing(2),
            marginTop: 20,
            backgroundColor: 'white',
            borderRadius: '100%',
          }}
        />
        <br />
        <Typography
          variant="h1"
          mt={2}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            textAlign: 'center',
            color: 'white',
            textTransform: 'capitalize',
          }}
        >
          WELCOME TO HIRING MADE EASY
        </Typography>
        <Typography
          textAlign="center"
          variant="h5"
          sx={{
            padding: { xs: '0', sm: '10px 40px' },
            lineHeight: '1.3',
            maxWidth: '500px',
            margin: '0 auto',
            color: 'white',
          }}
        >
          Your first job is created, but let's review things & make sure we get
          those applications flying in!
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            padding: { xs: '0', sm: '0px 40px' },
            lineHeight: '1.3',
            maxWidth: '500px',
            margin: '0 auto',
            color: 'white',
            fontStyle: 'italic',
          }}
        >
          (you can come back to this checklist anytime)
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: { md: '40px' } }}>
        <Stack mt={4}>
          {[
            ...stepArray.filter((_, index, arr) => {
              if (
                authData?.user?.company?.plan === 999
                  ? currentPlanData?.object?.plan_int === 1
                  : authData?.user?.company?.plan === 1
              ) {
                return index !== arr.length - 1;
              } else if (
                authData?.user?.company?.plan === 4 ||
                (authData?.user?.company?.plan === 999 &&
                  currentPlanData?.object?.plan_int === 4)
              ) {
                return index !== arr.length - 1 && index !== 1;
              }
              return true;
            }),
            ...(authData?.user?.company?.plan === 4 ||
            (authData?.user?.company?.plan === 999 &&
              currentPlanData?.object?.plan_int === 4)
              ? [
                  {
                    title: 'Step',
                    isChecked: isChecked,
                    description:
                      'Watch your inbox & ask for help if you need it',
                    buttonText: 'Disabled',
                    buttonAction: handleButtonClick,
                    items: [
                      {
                        type: 'We’ll send you helpfull info, but if you’re not hiring lighterning fast, let us know so we can get you there.',
                        action: () => {
                          handleButtonClick();
                        },
                        hasAction: inboxChecked ? true : false,
                      },
                    ],
                  },
                ]
              : []),
          ].map((step, index) => (
            <ChecklistStep
              key={index}
              index={index}
              step={step}
              selectedPlan={selectedPlan}
              handleCheckBoxClick={handleCheckBoxClick}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: '20px', justifyContent: 'center' }}>
        <Stack
          direction={{ sm: 'column', md: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ gap: '10px' }}
        >
          <Button
            sx={{ width: '200px' }}
            variant="contained"
            size="small"
            onClick={() => {
              track(
                `${amplitudeEventsName.CHECKLIST_CLOSE_FOR_NOW_BUTTON}-${selectedPlanName(currentPlanData?.object?.plan_int)}`,
              );
              handleClose();
            }}
          >
            Close For Now
          </Button>
          <Button
            sx={{ width: '200px' }}
            variant="outlined"
            size="small"
            color="error"
            onClick={() => {
              track(
                `${amplitudeEventsName.CHECKLIST_DONT_SHOW_AGAIN_BUTTON}-${selectedPlanName(currentPlanData?.object?.plan_int)}`,
              );
              updateWalkthroughStatus(undefined, { onSuccess: handleClose });
              updateStatus({ walkthrough_status: 1 });
            }}
          >
            Don't show again
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default BetterHiringChecklist;
